import { LanguageSettingOptions } from '@/hooks/useLanguageSetting';

type RouteLanguageOptions = Omit<LanguageSettingOptions, 'enabled'>;

/**
 * 사용자 언어 설정(MyInfo.language)을 i18n 에 자동 적용할 라우트.
 *
 * 기존에는 페이지마다 `useLanguageSetting` 을 직접 호출했고, 그 결과 PC 결재
 * (`/approval/**`) 처럼 호출이 빠진 화면은 번역본이 있어도 계속 한국어로 표시됐다
 * (PORTAL-13947). 여기 등록된 prefix 는 `_app` 의 `LanguageSync` 가 한 번에 적용하므로,
 * 같은 prefix 아래 새 페이지가 생겨도 페이지 쪽에서 따로 할 일이 없다.
 *
 * prefix 는 `router.pathname` 기준이며 완전 일치 또는 `prefix/` 하위만 매칭한다
 * (`/approval` 이 `/approvalXxx` 를 잡지 않도록).
 *
 * 라우트에 묶이지 않는 호출부는 여기 못 올린다 — `components/ErrorPage`(여러 화면의
 * 에러 상태), `components/Record/TalkFLow`(PC `/talkflow` 와 홈의 모달 양쪽)는
 * 각자 훅을 계속 호출한다.
 */
export const LANGUAGE_SYNCED_ROUTES: {
  prefix: string;
  options: RouteLanguageOptions;
}[] = [
  {
    // 결재 작성/편집/상세(viewer). 목록(`/?tab=work&subtab=approval`)은 아래 `/` 가 처리.
    prefix: '/approval',
    options: {
      // 결재는 zh 번역본(`translations/zh/approval.json`)이 있으므로 중국어를
      // 영어로 대체하지 않는다. 없는 문구는 i18n fallbackLng 로 ko 로 내려간다.
      isZhEn: false,
      // PC 결재라 RN 의 `?lang=en` 우선 규칙은 적용하지 않는다
      // (모바일 결재 `/m/approval` 는 아래 `/m` 이 `rnLanguageIsFirst: true` 로 처리).
      rnLanguageIsFirst: false,
    },
  },
  {
    // `pages/organization/index` 는 `[...org]` 로 redirect 하며 null 만 렌더해 영향 없음
    prefix: '/organization',
    options: { isZhEn: false, rnLanguageIsFirst: false },
  },
  {
    // `/gpt/faq`, `/gpt/employee`, `/gpt/employee/[keyword]`.
    // 모바일 `/m/gpt/faq` 는 여기 안 걸리고 아래 `/m` 이 잡는다.
    prefix: '/gpt',
    options: { isZhEn: false, rnLanguageIsFirst: false },
  },
  {
    // 모바일 전체. RN 웹뷰라 `?lang=en` 우선, zh 번역본을 아직 안 쓰므로 영어로 대체한다.
    // (`translations/zh` 가 `ko` 와 네임스페이스가 같아진 뒤로 `isZhEn: true` 가 여전히
    //  맞는 정책인지는 제품 판단 대상 — 지금은 기존 동작을 그대로 유지한다.)
    prefix: '/m',
    options: { isZhEn: true, rnLanguageIsFirst: true },
  },
  {
    // 홈(`pages/index`). `prefix: '/'` 는 `pathname === '/'` 로만 매칭된다 —
    // `startsWith('//')` 가 성립하지 않아 전체 라우트를 삼키지 않는다. 의도된 동작이므로
    // 아래 matcher 를 고칠 때 이 항목이 모든 경로를 먹지 않는지 반드시 같이 확인할 것.
    prefix: '/',
    options: { isZhEn: false, rnLanguageIsFirst: false },
  },
];

export const resolveLanguageSettingOptions = (
  pathname: string,
): RouteLanguageOptions | undefined =>
  LANGUAGE_SYNCED_ROUTES.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )?.options;
